import type { AppContribution } from "@/composition";
import { type Component, createMemo, For, Show } from "solid-js";
import { Link, useLocation, useNavigate } from "@tanstack/solid-router";
import { cn } from "@jornada/shared";
import {
  Bug,
  ChevronsUpDown,
  ClipboardList,
  LayoutDashboard,
  LogOut,
  Mail,
  MonitorSmartphone,
  Palette,
  Scan,
  Shield,
  ShieldAlert,
  ShieldCheck,
  Trophy,
  Type,
  User,
  UserCircle,
  Users,
} from "@fleet/icons";

import { Avatar } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Separator } from "@fleet/ui";
import { Logo } from "@/components/ui/logo";
import { SidebarNavItem } from "@/components/ui/sidebar-nav-item";
import { safeRedirectTarget, useSession } from "@/lib/auth";
import { useAccounts } from "@/features/auth/use-accounts";
import { useI18n } from "@/providers/i18n-provider";
import { useUserPreferences } from "@/providers/user-preferences-context";
import { useSignOut } from "@/providers/boot-manager";
import { useKernel } from "@fleet/kernel";
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";

const inactiveClass =
  "text-muted-foreground/80 hover:bg-accent/5 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/30 focus-visible:bg-accent/5 transition-[background-color,color,box-shadow]";

export interface SidebarStats {
  /** Timesheets pending review (badge in Review). */
  pendingReviews: number;
}

interface NavItemDef {
  to: string;
  label: MessageDescriptor;
  icon: Component<{ class?: string }>;
  key?: string;
}

interface AppSidebarProps {
  stats: () => SidebarStats | undefined;
  onCloseSidebar?: () => void;
}

const NAV_ITEMS: NavItemDef[] = [
  { to: "/", label: msg`Overview`, icon: LayoutDashboard, key: "overview" },
  { to: "/team", label: msg`Team`, icon: Users, key: "team" },
  { to: "/review", label: msg`Review`, icon: ClipboardList, key: "review" },
  { to: "/transparency", label: msg`Transparency`, icon: ShieldCheck, key: "transparency" },
  { to: "/devices", label: msg`Devices`, icon: MonitorSmartphone, key: "devices" },
];

const TEAM_ITEMS: NavItemDef[] = [
  { to: "/organizations", label: msg`Organizations`, icon: Users },
  { to: "/invitations", label: msg`Invitations`, icon: Mail },
];

const ADMIN_ITEMS: NavItemDef[] = [
  { to: "/admin/storage", label: msg`Storage`, icon: Shield },
  { to: "/admin/audit", label: msg`Audit`, icon: Scan },
  { to: "/admin/themes", label: msg`Themes`, icon: Palette },
  { to: "/admin/theme-audit", label: msg`Theme Audit`, icon: ShieldAlert },
  { to: "/admin/fonts", label: msg`Fonts`, icon: Type },
];

export function AppSidebar(props: AppSidebarProps) {
  const session = useSession();
  const location = useLocation();
  const { t } = useI18n();
  const { preferences } = useUserPreferences();
  const { promptSignOut } = useSignOut();
  const navigate = useNavigate();
  const accounts = useAccounts();
  const kernelApp = useKernel<AppContribution>();

  const contributedNav = createMemo(() =>
    kernelApp()
      .contributions.filter(
        (c): c is Extract<import("@/composition").AppContribution, { kind: "nav" }> =>
          c.kind === "nav",
      )
      .map((c) => c.item)
      .sort((a, b) => a.order - b.order),
  );

  // URL-addressable switcher (#15b pattern): `?account=switch` opens it. The
  // overlay itself is mounted once at the layout level (AccountSwitcherHost);
  // this trigger just flips the URL flag for THIS tab's `/u/<n>`.
  const openSwitcher = () => {
    props.onCloseSidebar?.();
    void navigate({
      to: ".",
      search: (prev) => ({ ...prev, account: "switch" }),
      replace: true,
    });
  };

  // Reactive view of the active account for the switcher trigger label.
  const activeAccount = createMemo(() => accounts.active());
  const activeLabel = createMemo(() => {
    const a = activeAccount();
    if (!a || a.kind === "guest") {
      return t(msg`Guest`);
    }
    return a.displayName || a.email;
  });
  // The ACTIVE account photo comes from the local row (reactive and offline); the
  // account registry is only the initial value, because it is hydrated from
  // `get-session` and is not refreshed on every write.
  const activeImage = () => preferences()?.image ?? null;

  const getCount = (key: string | undefined): number | undefined => {
    if (key === undefined) {
      return undefined;
    }
    const s = props.stats();
    if (!s) {
      return undefined;
    }
    switch (key) {
      case "review": {
        return s.pendingReviews;
      }
      default: {
        return 0;
      }
    }
  };

  const handleSignOut = () => {
    props.onCloseSidebar?.();
    promptSignOut();
  };

  return (
    <div class="flex flex-col h-full pt-[var(--safe-area-inset-top)] pb-[var(--safe-area-inset-bottom)] pl-[var(--safe-area-inset-left)]">
      <div class="flex h-16 items-center px-6 shrink-0">
        <Link
          to="/"
          class="flex items-center transition-transform hover:scale-[1.02] active:scale-95"
          onClick={() => props.onCloseSidebar?.()}
        >
          <Logo size="sm" />
        </Link>
      </div>

      <Separator />

      <nav
        aria-label={t(msg`Main navigation`)}
        class="flex flex-1 flex-col gap-0.5 overflow-y-auto p-2 scrollbar-hide"
      >
        <For each={NAV_ITEMS}>
          {(item) => (
            <SidebarNavItem
              to={item.to}
              label={t(item.label)}
              icon={item.icon}
              count={getCount(item.key)}
              exact={item.to === "/"}
              onClick={() => props.onCloseSidebar?.()}
            />
          )}
        </For>
        <For each={contributedNav()}>
          {(item) => (
            <SidebarNavItem
              to={item.to}
              label={t(item.label)}
              icon={item.icon}
              onClick={() => props.onCloseSidebar?.()}
            />
          )}
        </For>

        <div class="mt-4 px-3 mb-1">
          <h3 class="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
            {t(msg`Team`)}
          </h3>
        </div>
        <For each={TEAM_ITEMS}>
          {(item) => (
            <SidebarNavItem
              to={item.to}
              label={t(item.label)}
              icon={item.icon}
              onClick={() => props.onCloseSidebar?.()}
            />
          )}
        </For>

        <Show when={session.data?.user.role === "admin"}>
          <div class="mt-4 px-3 mb-1">
            <h3 class="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
              {t(msg`Administration`)}
            </h3>
          </div>
          <For each={ADMIN_ITEMS}>
            {(item) => (
              <SidebarNavItem
                to={item.to}
                label={t(item.label)}
                icon={item.icon}
                onClick={() => props.onCloseSidebar?.()}
              />
            )}
          </For>
        </Show>
      </nav>

      <Separator />

      {/* Bottom actions */}
      <div class="flex flex-col gap-0.5 p-2 shrink-0">
        {/* ── Account switcher (multi-account #18) ────────────────────────── */}
        <button
          type="button"
          onClick={openSwitcher}
          class={cn(
            "flex w-full items-center gap-2.5 rounded-md px-3 py-2.5 text-sm transition-colors",
            inactiveClass,
          )}
          aria-label={t(msg`Switch account`)}
          data-testid="account-switcher-trigger"
        >
          <Show
            when={activeAccount()?.kind === "real" ? activeAccount() : null}
            fallback={
              <span class="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground">
                <UserCircle class="h-4 w-4" />
              </span>
            }
          >
            {(account) => (
              <Avatar
                userId={account().userId}
                name={account().displayName}
                email={account().email}
                image={activeImage() ?? account().avatar}
                size="xs"
                class="h-7 w-7 text-xs"
                testId="sidebar-avatar"
              />
            )}
          </Show>
          <span class="min-w-0 flex-1 text-left">
            <span class="block truncate font-medium text-foreground">{activeLabel()}</span>
            <Show when={activeAccount()?.email}>
              <span class="block truncate text-xs text-muted-foreground/80">
                {activeAccount()?.email}
              </span>
            </Show>
          </span>
          <ChevronsUpDown class="h-4 w-4 shrink-0 opacity-60" />
        </button>
        <Separator class="my-1" />
        <SidebarNavItem
          to="/achievements"
          label={t(msg`Achievements`)}
          icon={Trophy}
          onClick={() => props.onCloseSidebar?.()}
        />
        <SidebarNavItem
          to="/profile"
          label={t(msg`Profile`)}
          icon={User}
          onClick={() => props.onCloseSidebar?.()}
        />
        <SidebarNavItem
          to="/debug"
          label={t(msg`Debug`)}
          icon={Bug}
          onClick={() => props.onCloseSidebar?.()}
        />

        <Show
          when={session.isAuthenticated()}
          fallback={
            <Link
              to="/login"
              search={{ redirect: safeRedirectTarget(location().pathname) }}
              onClick={() => props.onCloseSidebar?.()}
              class={cn(
                "mt-1 flex items-center gap-2.5 rounded-md px-3 py-2.5 text-sm font-medium transition-colors",
                "bg-primary/10 text-primary hover:bg-primary/20",
              )}
            >
              <User class="h-4 w-4" />
              {t(msg`Sign In`)}
            </Link>
          }
        >
          <Button
            variant="ghost"
            class={`mt-1 w-full justify-start gap-2.5 px-3 py-2.5 ${inactiveClass} hover:text-destructive`}
            onClick={handleSignOut}
          >
            <LogOut class="h-4 w-4" />
            {t(msg`Sign Out`)}
          </Button>
        </Show>
      </div>
    </div>
  );
}
